import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Clock, Eye, User, Tag, ExternalLink } from "lucide-react";
import { NewsCard } from "@/components/NewsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = {
  "stand-tasarimi": "Stand Tasarımı",
  "lojistik": "Lojistik",
  "sektor-trendleri": "Sektör Trendleri",
  "teknoloji": "Teknoloji",
  "surdurulebilirlik": "Sürdürülebilirlik",
  "pazar-analizi": "Pazar Analizi",
};

export default function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/articles/${id}`);
        setArticle(res.data);
        const relRes = await axios.get(
          `${API}/articles?category=${res.data.category}&limit=4`
        );
        setRelated(relRes.data.filter((a) => a.id !== id).slice(0, 3));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
    window.scrollTo(0, 0);
  }, [id]);

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 md:px-8 lg:px-12 py-24">
        <div className="skeleton h-8 w-32 mb-8" />
        <div className="skeleton h-12 w-3/4 mb-4" />
        <div className="skeleton h-6 w-1/2 mb-8" />
        <div className="skeleton h-96 w-full mb-8" />
        <div className="skeleton h-4 w-full mb-2" />
        <div className="skeleton h-4 w-5/6 mb-2" />
        <div className="skeleton h-4 w-4/5 mb-2" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 md:px-8 lg:px-12 py-24 text-center">
        <h1 className="text-3xl font-bold mb-4">Makale Bulunamadı</h1>
        <Link to="/" className="text-[#1B4F72] hover:underline">
          Ana sayfaya dön
        </Link>
      </div>
    );
  }

  const paragraphs = article.content.split("\n\n").filter(Boolean);

  return (
    <div data-testid="article-page" className="pb-24">
      {/* Back Link */}
      <div className="max-w-screen-xl mx-auto px-4 md:px-8 lg:px-12 pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-stone-500 hover:text-stone-900 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Ana Sayfa
        </Link>
      </div>

      {/* Article Header */}
      <article className="max-w-screen-xl mx-auto px-4 md:px-8 lg:px-12 pt-8">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <Badge
              variant="outline"
              className="font-mono text-xs uppercase tracking-widest border-[#1B4F72] text-[#1B4F72]"
              data-testid="article-category-badge"
            >
              {CATEGORIES[article.category] || article.category}
            </Badge>
            <span className="font-mono text-xs text-stone-500 uppercase tracking-widest">
              {article.source}
            </span>
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95] text-stone-950 mb-6"
            data-testid="article-title"
          >
            {article.title}
          </h1>

          <p className="text-lg md:text-xl text-stone-600 leading-relaxed mb-8 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            {article.summary}
          </p>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-stone-500 font-mono mb-8">
            <span className="flex items-center gap-2">
              <User className="w-3.5 h-3.5" />
              {article.author}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" />
              {formatDate(article.published_at)}
            </span>
            <span className="flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" />
              {article.views.toLocaleString("tr-TR")} görüntülenme
            </span>
          </div>
        </div>

        <Separator className="mb-8" />

        {/* Hero Image */}
        <div className="max-w-4xl aspect-[21/9] overflow-hidden mb-12 border border-stone-200">
          <img
            src={article.image_url}
            alt={article.title}
            className="w-full h-full object-cover"
            data-testid="article-hero-image"
          />
        </div>

        {/* Article Content */}
        <div className="max-w-3xl article-content" data-testid="article-content">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {/* Source Attribution (when show_source is true) */}
        {article.show_source && article.source_url && (
          <div className="max-w-3xl mt-8 p-5 bg-stone-50 border border-stone-200">
            <p className="text-sm text-stone-600 mb-3">
              Bu haber <strong>{article.source}</strong> kaynaklı olup, özet olarak yayınlanmıştır.
            </p>
            <a
              href={article.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1B4F72] text-white text-sm font-mono uppercase tracking-widest hover:bg-[#154360] transition-colors"
              data-testid="source-link"
            >
              <ExternalLink className="w-4 h-4" />
              Haberin Devamını Oku — {article.source}
            </a>
          </div>
        )}

        {/* Gallery Images */}
        {article.gallery_images && article.gallery_images.length > 0 && (
          <div className="max-w-4xl mt-12">
            <div className="grid grid-cols-4 gap-2">
              {article.gallery_images.slice(0, 8).map((img, i) => (
                <div key={i} className="aspect-[4/3] overflow-hidden bg-stone-100">
                  <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 cursor-pointer" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="max-w-3xl mt-12 pt-8 border-t border-stone-200">
            <div className="flex items-center gap-2 mb-4 text-stone-500">
              <Tag className="w-4 h-4" />
              <span className="font-mono text-xs uppercase tracking-widest">Etiketler</span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="article-tags">
              {article.tags.map((tag) => (
                <span key={tag} className="category-tag text-[10px]">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="max-w-screen-xl mx-auto px-4 md:px-8 lg:px-12 mt-24">
          <Separator className="mb-12" />
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-8" data-testid="related-articles-heading">
            İlgili Haberler
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {related.map((a) => (
              <NewsCard key={a.id} article={a} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
